import { env } from '../../config';
import { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import Switch from "react-switch";
import { HexColorInput } from "react-colorful";
import { PopoverPicker } from '../ColorPicker/PopoverPicker';
import dropdownOptions from './dropdownOptions.json';
import constants from './constants.json';
import dragIcon from '../../images/icons/contentManagement/drag-icon.svg'
import { getSlideById, userHasPermissionForLocation } from './utils';

const BrandingConfiguration = ({
    state,
    setState,
    setStateChanged,
    oneViewOptions,
    permissions,
    purchasedProducts,
    apiKey,
    handleContentModalOpen,
    user
}) => {
    const [iframeUrl, setIframeUrl] = useState('');
    const [backgroundSlide, setBackgroundSlide] = useState({});
    const [selectedRadio, setSelectedRadio] = useState(backgroundSlide.url? 'backgroundImage' : 'mainBg');

    const configs = state?.playlist?.brandingConfigs;
    const defaultConfigs = constants.DEFAULT_BRANDING_CONFIGS;
    const interactivityDisabled = !purchasedProducts?.interactivityConfiguration;

    useEffect(() => {
        const baseUrl = `${env.CITYMO_URL}/partner?key=${apiKey}&coordinates=${state.playlist?.location?.latitude},${state.playlist?.location?.longitude}&zoom=50${interactivityDisabled ? '' : '&barPosition=bottom&menu=anywhere'}`;
        setIframeUrl(baseUrl + getBrandingParams());
    }, [apiKey, state.playlist?.location, configs, selectedRadio, backgroundSlide.url]); // update when these change

    useEffect(() => {
        // Get background slide id if it exists
        if (state.playlist?.brandingConfigs?.backgroundImageId) {
            getSlideById(state.playlist.brandingConfigs.backgroundImageId)
            .then((slide) => {
                setBackgroundSlide(slide);
                setSelectedRadio('backgroundImage');
            })
            .catch((error) => {
                defaultBgSettings();
            });
        } else {
            defaultBgSettings();
        }
    }, [state.playlist?.id]);

    const defaultBgSettings = () => {
        setBackgroundSlide({});
        setSelectedRadio('mainBg');
    }

    const handleColorChange = (property, value) => {
        //Making sure value does not come in with a # at the start
        const color = value.length > 6 ? value.slice(1) : value;
        const tempConfigs = {...state.playlist.brandingConfigs};
        tempConfigs[property] = color;

        setState({
            ...state,
            playlist: {...state.playlist, brandingConfigs: tempConfigs}
        });
        setStateChanged(true);
    }

    const handleConfigChanges = (propNames, values) => {
        const tempConfigs = {...state.playlist.brandingConfigs};
        propNames.forEach((item, index) => {
            tempConfigs[item] = values[index];
        });
        setState({
            ...state,
            playlist: {...state.playlist, brandingConfigs: tempConfigs}
        });
        setStateChanged(true);
    }

    const isBrandingEnabled = (key) => {
        let retval = false;
        let interactivityConfigs = state?.playlist?.interactivityConfigs || {};
        for (let [configKey, value] of Object.entries(interactivityConfigs)) {
            if (configKey === key) {
                retval = value;
            }
        }

        return retval;
    }

    const openContentLibraryModal = () => {
        handleContentModalOpen(
			'shared_folder',
			null,
			null,
            // callback function after selecting slide
			(slide) => {
                if (slide.id) {
                    let mainBg = state.playlist?.brandingConfigs?.mainBg;
                    // Switching to a transparent color
                    mainBg = mainBg.slice(0, 6) + '00'

                    const tempConfigs = {
                        ...state.playlist.brandingConfigs,
                        backgroundImageId: slide.id,
                        mainBg: mainBg // Set background to transparent when selecting image
                    };
                    setState({
                        ...state,
                        playlist: {...state.playlist, brandingConfigs: tempConfigs}
                    });
                    setStateChanged(true);
                    setBackgroundSlide(slide);
                    setSelectedRadio('backgroundImage');
                }
                return;
            }
		);
    }

    const brandingEditorDisabled = () => {
        return !brandingEditorEnabled();
    }

    const brandingEditorEnabled = () => {
        return 'brandingConfiguration' in permissions
            && state?.playlist?.location?.id in permissions['brandingConfiguration']
            && permissions['brandingConfiguration'][state.playlist.location.id].includes('editor');
    }

    const getLabelAndColorPicker = (label, key, showRadioButton, isDisabled) => {
        const onChange = (colorVal) => handleColorChange(key, colorVal);
        const brandingVal = configs && configs[key] ? configs[key] : defaultConfigs[key]

        return <div className="input-container">
            <div className="label">{label}:</div>
            <input
                type="radio"
                name={`radio-${key}`}
                value={key}
                checked={selectedRadio === key}
                disabled={brandingEditorDisabled() || isDisabled}
                style={{ marginRight: '1.25em', visibility: showRadioButton ? 'visible' : 'hidden' }}
                onChange={() => {
                    setSelectedRadio(key);
                    setStateChanged(true);

                    let newVal = brandingVal;
                    // Check if we are switching to a transparent color
                    if (brandingVal.length >= 8 && brandingVal.slice(-2) === '00') {
                        newVal = brandingVal.slice(0, 6);
                    }

                    setBackgroundSlide({});

                    setState({
                        ...state,
                        playlist: {
                            ...state.playlist,
                            brandingConfigs: {
                                ...state.playlist.brandingConfigs,
                                backgroundImageId: null,
                                [key]: newVal // Remove alpha if it is '00'
                            }
                        }
                    }); 
                }}
            />
            <HexColorInput
                className="input-color"
                color={brandingVal}
                onChange={onChange}
                prefixed={true}
                alpha={true}
                disabled={brandingEditorDisabled() || isDisabled}
            />
            <PopoverPicker
                color={'#' + brandingVal}
                onChange={brandingEditorEnabled() ? onChange : null}
                disabled={brandingEditorDisabled() || isDisabled}
                alpha={true}
            />
        </div>
    }

    // Radio text to show while other items are in development
    const getRadioText = (label, message, radioValue) => {
        return <div className="input-container" style={{ width: '30em', alignItems: 'flex-start' }}>
            <div className="label">{label}</div>
            <input
                type="radio"
                name="background-radio"
                value={radioValue}
                checked={selectedRadio === radioValue}
                disabled={brandingEditorDisabled() || !userHasPermissionForLocation('contentLibrary', permissions, state.playlist?.location?.id)}
                style={{ marginRight: '1.25em', visibility: 'visible' }}
                onChange={() => {
                    setSelectedRadio(radioValue);
                    setStateChanged(true);

                    let mainBg = state.playlist?.brandingConfigs?.mainBg;
                    // Switching to a transparent color
                    mainBg = mainBg.slice(0, 6) + '00'

                    setState({
                        ...state,
                        playlist: {
                            ...state.playlist,
                            brandingConfigs: {
                                ...state.playlist.brandingConfigs,
                                backgroundImageId: state.playlist?.brandingConfigs?.backgroundImageId,
                                mainBg: mainBg // Set background to transparent when switching to image
                            }
                        }
                    }); 
                }}
            />
            <div
                className="placeholder-image"
                onClick={brandingEditorEnabled() && userHasPermissionForLocation('contentLibrary', permissions, state.playlist?.location?.id) ? openContentLibraryModal : null}
                style={
                    !brandingEditorEnabled() || !userHasPermissionForLocation('contentLibrary', permissions, state.playlist?.location?.id)
                    ? {cursor: 'default', color: '#ccd2d3'}
                    : null
                }
            >
                {
                    backgroundSlide.url
                    ? backgroundSlide.url.includes(env.S3_MESSAGING_BUCKET) 
                        ? <IFrame url={backgroundSlide.url} type={'image'} /> 
                        : <IFrame url={backgroundSlide.url} type={'url'} />
                    : <div className="placeholder-content">
                        <img src={dragIcon} />
                        <div>Click here to select background image</div>
                    </div>
                }
            </div>
        </div>
    }

    const getLabelAndSwitch = (label, key) => {
        let onChange = (checked) => handleConfigChanges([key], [checked]);

        return <div className="input-container">
            <div className="label">{label}:</div>
            <Switch
                className="switch"
                checked={ configs && key in configs ? configs[key] : defaultConfigs[key] }
                onChange={onChange}
                onColor="#2386EE"
                uncheckedIcon
                checkedIcon={false}
                disabled={brandingEditorDisabled()}
                height={20}
                width={32}
            />
        </div>
    }

    const getSwitchWithColorPicker = (label, key, switchKey) => {
        let onChange = (checked) => handleConfigChanges([switchKey], [checked]);
        let onColorChange = (colorVal) => handleColorChange(key, colorVal);

        // Determine if the switch is checked
        const isSwitchChecked = configs && switchKey in configs ? configs[switchKey] : defaultConfigs[switchKey];

        return <div className="input-container">
            <div className="label">{label}:</div>
            <Switch
                className="switch"
                checked={isSwitchChecked}
                onChange={onChange}
                onColor="#2386EE"
                uncheckedIcon
                checkedIcon={false}
                disabled={brandingEditorDisabled()}
                height={20}
                width={32}
            />
            <HexColorInput
                className="input-color"
                color={configs && configs[key] ? configs[key] : defaultConfigs[key]}
                onChange={onColorChange}
                prefixed={true}
                alpha={true}
                disabled={!isSwitchChecked || brandingEditorDisabled()}
            />
            <PopoverPicker
                color={configs && configs[key] ? '#' + configs[key] : '#' + defaultConfigs[key]}
                onChange={brandingEditorEnabled() ? onColorChange : null}
                disabled={!isSwitchChecked || brandingEditorDisabled()}
                alpha={true}
            />

        </div>
    }

    const getLabelAndSelect = (label, dropdownKey, configKey, showRadioButton) => {
        let onChange = (e) => handleConfigChanges([configKey], [e.value]);

        return <div className="input-container">
            <div className="label">{label}:</div>
            <input
                type="radio"
                name={`radio-${configKey}`}
                checked={true}
                disabled={brandingEditorDisabled()}
                style={{ marginRight: '1.25em', visibility: showRadioButton ? 'visible' : 'hidden' }}
                readOnly={true}
            />
            <Select
                onChange={onChange}
                options={dropdownOptions[dropdownKey]}
                value={dropdownOptions[dropdownKey].find(option => option.value === (configs ? configs[configKey] : defaultConfigs[configKey]))}
                className={"dropdown-list"}
                defaultValue={dropdownOptions[dropdownKey][0]}
                styles={basicConfigDropdownStyling}
                isDisabled={brandingEditorDisabled()}
            />
        </div>
    }

    /* React-Select custom styling */
    const basicConfigDropdownStyling = {
        container: (baseStyles, state) => ({
            ...baseStyles,
            // Too much gray if the dropdown is disabled.
            boxShadow: state.isDisabled ? "unset" : "0px 2px 8px 4px rgba(0, 0, 0, 0.07)",
            borderRadius: "8px",
            width: "11.5em",
        }),
        control: (baseStyles, state) => ({
            ...baseStyles,
            border: "none",
            cursor: "pointer",
            width: "11.5em",
        }),
        indicatorSeparator: (baseStyles, state) => ({
            ...baseStyles,
            display: "none",
            width: "11.5em",
        }),
        option: (baseStyles, state) => ({
            ...baseStyles,
            color: state.isDisabled ? "#919191" : "inherit",
            cursor: "pointer",
            width: "11.5em",
        }),
        menu: (baseStyles) => ({
            ...baseStyles,
            width: "11.5em"
        }),
    };

    const getBrandingParams = () => {
        const keys = [
            selectedRadio === 'mainBg' ? 'mainBg' : null,
            'mainText', 'accentColor',
            'cardHeader', 'ui', 'cardBg', 'cardText', 'cardShadow', 'borderRounding',
            'borderColor', 'rowColor',
            'navBg', 'navText', 'menuBg', 'menuText'
        ];
        const params = [];

        keys.forEach(key => {
            if (!configs || configs[key] === undefined || configs[key] === null || configs[key] === '') return;

            if (key === 'borderColor' && !configs.enableBorder) return;
            if (key === 'rowColor' && !configs.enableRowLines) return;

            params.push(`${encodeURIComponent(key)}=${encodeURIComponent(configs[key])}`);
        });

        return params.length ? '&' + params.join('&') : '';
    };

    return (
        <div className="BrandingConfiguration">
            <div className="photo"></div>
            <div className="configuration">
                <h2>Branding</h2>
                <div className="columns-wrapper" style={{ display: 'flex', gap: '2em', alignItems: 'flex-start' }}>
                    <div className="left-column" style={{ flex: 1 }}>
                        
                        <div>
                            <h3>Primary colors and style</h3>
                            <div className="description">
                                These settings apply to anything on the page outside of a "card" (see next section).
                            </div>
                            <div className="primary">
                                <div>
                                    { getLabelAndColorPicker('Background', 'mainBg', true) }
                                    { getRadioText('', 'Background images coming soon', 'backgroundImage') }
                                    { getLabelAndColorPicker('Text', 'mainText') }
                                    { getLabelAndColorPicker('Accents', 'accentColor') }
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3>Information "cards"</h3>
                            <div className="description">
                                “Cards” are boxes of information tiled on your Screen or Mobile. 
                            </div>
                            <div className="primary">
                                 <div>
                                    { getLabelAndSwitch('Header', 'cardHeader') }
                                    { getLabelAndSelect('Icon style', 'UI', 'ui')}
                                    { getLabelAndColorPicker('Background', 'cardBg') }
                                    { getLabelAndColorPicker('Text', 'cardText') }
                                    { getSwitchWithColorPicker('Border', 'borderColor', 'enableBorder') }
                                    { getSwitchWithColorPicker('Row lines', 'rowColor', 'enableRowLines') }
                                    { getLabelAndSwitch('Shadow', 'cardShadow') }
                                    { getLabelAndSwitch('Soft corners', 'borderRounding') }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="right-column">
                        <div className="card-container">
                            <iframe
                                src={iframeUrl}
                                title="CityMotion Preview"
                                width="100%"
                                height="100%"
                                className="branding-preview-iframe"
                                style={{
                                    border: "none",
                                    borderRadius: "8px",
                                    display: "block"
                                }}
                                allowFullScreen
                            />
                        </div>
                        <p style={{ marginLeft: '5em', marginTop: '0.5em' }}>This is a preview of your branding settings. Colors may look slightly different depending on the display or device. We recommend checking colors on the intended display for accuracy.</p>
                    </div>
                </div>
                <h2>Branding for interactivity features</h2>
                <div className="columns-wrapper" style={{ display: 'flex', gap: '2em', alignItems: 'flex-start' }}>
                    <div className="left-column" style={{ flex: 1 }}>
                        <div className={`${state?.playlist?.product === 'screen_pro' && !isBrandingEnabled('menuEnabled') ? 'disabled-section' : ''}`}>
                            <h3>Information and search bar</h3>
                            <div className="description"> Available only when enabled via the Interactivity features view.</div>
                            <div className="searchAndWeather">
                                <div>
                                    { getLabelAndColorPicker('Background', 'menuBg', false, interactivityDisabled) }
                                    { getLabelAndColorPicker('Text', 'menuText', false, interactivityDisabled) }
                                </div>
                            </div>
                        </div>

                        <div className={`${state?.playlist?.product === 'screen_pro' && !isBrandingEnabled('navBarEnabled') ? 'disabled-section' : ''}`}>
                            <h3>Navigation bar</h3>
                            <div className="description">Available only when enabled via the Interactivity features view.</div>
                            <div className="modeAndTripPlanner">
                                <div>
                                    { getLabelAndColorPicker('Background', 'navBg', false, interactivityDisabled) }
                                    { getLabelAndColorPicker('Text', 'navText', false, interactivityDisabled) }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="right-column">
                    </div>
                </div>
            </div>
        </div>
    );
}

const IFrame = ({ url, type }) => {
    const ref = useRef();

    return type === 'image' ? <img src={url} ref={ref} className="background-image"></img> : <iframe src={url} ref={ref} className="background-image"></iframe>
}

export default BrandingConfiguration;
